import React from 'react'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { chevronBack, informationCircle } from 'ionicons/icons'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import SegmentedSelect, {
  ISegmentData,
  ISegmentOption,
} from '../../../components/segmentedSelect/SegmentedSelect.component'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getDiagnosisOptionsSelector } from '../../options/selectors/getDiagnosisOptions.selector'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { getDiagnosisSelector } from '../selectors/getDiagnosis.selector'
import { setSymptoms, setSymptomsPNNVs } from '../store/diagnosis.actions'
import { IDiagnosisState } from '../store/diagnosis.types'
import { DiagnosisConstants } from '../store/diagnosis.constants'

const Symptoms: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const diagnosisState: IDiagnosisState = useSelector(getDiagnosisSelector)
  const { symptoms, symptomsPNNVs } = diagnosisState

  const diagnosisOptions = useSelector(getDiagnosisOptionsSelector)
  const { symptomsOptions } = diagnosisOptions

  const onBackClick = () => {
    save()
  }

  const save = () => {
    if (isSaveValid()) history.push(TabConstants.CC_HPI)
  }

  const segmentsMetdaData: ISegmentData[] = [
    {
      id: +DiagnosisConstants.PRIMARY_SEGMENT_ID,
      name: t('diagnosis.primary'),
      type: 'single',
      color: '#7145A9',
    },
    {
      id: +DiagnosisConstants.OTHER_SEGMENT_ID,
      name: t('diagnosis.other'),
      type: 'multi',
      color: '#599199',
    },
    {
      id: +DiagnosisConstants.NOT_PRESENT_SEGMENT_ID,
      name: t('diagnosis.notPresent'),
      type: 'multi',
      color: '#99595E',
    },
  ]

  const onchange = (values: ISegmentOption[]) => {
    saveValues(values)
  }

  const clearSelectedValues = () => {
    dispatch(setSymptoms([]))
    dispatch(setSymptomsPNNVs([]))
  }

  const isSaveValid = () => {
    const primaryValues = symptoms.filter((value) => value.segmentId === DiagnosisConstants.PRIMARY_SEGMENT_ID)
    const otherValues = symptoms.filter((value) => value.segmentId === DiagnosisConstants.OTHER_SEGMENT_ID)
    if (otherValues.length > 0 && primaryValues.length < 1) {
      dispatch(
        showAlert({
          message: t('diagnosis.primaryNotPresentError'),
        })
      )
      return false
    }
    return true
  }

  const saveValues = (values: ISegmentOption[]) => {
    const pnnvValues = values.filter((value) => value.segmentId === DiagnosisConstants.NOT_PRESENT_SEGMENT_ID)
    const primayOtherValue = values.filter((value) => value.segmentId !== DiagnosisConstants.NOT_PRESENT_SEGMENT_ID)
    dispatch(setSymptoms(primayOtherValue))
    dispatch(setSymptomsPNNVs(pnnvValues))
  }

  const getSelectedValues = () => {
    return [...(symptoms || []), ...(symptomsPNNVs || [])]
  }

  const infoPopupContent = () => {
    return (
      <IonList>
        <IonItem lines='none'>
          <IonIcon className='info-icon-margin' slot='start' icon={informationCircle}></IonIcon>
          <IonLabel>
            <h1>
              {t('diagnosis.symptoms')} {t('diagnosis.types')}
            </h1>
          </IonLabel>
        </IonItem>

        <IonItem lines='none'>
          <IonLabel>
            <h3>{t('diagnosis.primary')}</h3>
            <p style={{ whiteSpace: 'break-spaces' }}>{t('diagnosis.primarySymptomsDescr')}</p>
          </IonLabel>
        </IonItem>

        <hr />

        <IonItem lines='none'>
          <IonLabel>
            <h3>{t('diagnosis.other')}</h3>
            <p style={{ whiteSpace: 'break-spaces' }}>{t('diagnosis.otherSymptomsDescr')}</p>
          </IonLabel>
        </IonItem>

        <hr />

        <IonItem lines='none'>
          <IonLabel>
            <h3>{t('diagnosis.notPresent')}</h3>
            <p>{t('diagnosis.notPresentSymptomsDescr')}</p>
          </IonLabel>
        </IonItem>
      </IonList>
    )
  }

  return (
    <IonPage>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' color='primary' onClick={onBackClick}>
              <IonIcon icon={chevronBack} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonTitle data-testid='HeaderTitle'>{t('diagnosis.symptoms')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color='light' fullscreen>
        <SegmentedSelect
          options={symptomsOptions}
          segmentsData={segmentsMetdaData}
          values={getSelectedValues()}
          onChange={onchange}
          infoPopupContent={infoPopupContent()}
        />
      </IonContent>
      <IonFooter className='ion-no-border'>
        <IonToolbar color='light' className='p-10'>
          <IonButtons>
            <IonButton
              data-testid='ClearAll'
              className='btn mr-8'
              color='danger'
              fill='outline'
              expand='block'
              onClick={() => clearSelectedValues()}>
              {t('common.buttons.ClearAll')}
            </IonButton>
            <IonButton
              data-testid='MultiSelectSave'
              disabled={false}
              className='btn btn-primary'
              expand='block'
              onClick={() => save()}
              id='multiselect-modal-save-button'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  )
}

export default Symptoms
